import { scheduleApi } from '@/api/schedule'
import { buildDraftOccurrencesFromSnapshot } from '@/api/schedule'
import { useScheduleStore } from '@/stores/schedule'
import { useScheduleDraft } from '@/composables/useScheduleDraft'
import type {
  ScheduleOccurrenceDraft,
  ScheduleSnapshotDetail,
  ScheduleSnapshotDiagnosis,
  ScheduleSnapshotDraftRequest,
  ScheduleSnapshotOccurrenceRequest
} from '@/types'

type UseScheduleSnapshotsReturn = {
  buildSnapshotPayload: (
    occurrences: ScheduleOccurrenceDraft[]
  ) => ScheduleSnapshotOccurrenceRequest[]
  diagnoseCurrentDraft: () => Promise<ScheduleSnapshotDiagnosis | null>
  saveSnapshot: (options?: {
    name?: string
    remark?: string
    skipDiagnose?: boolean
  }) => Promise<ScheduleSnapshotDetail | null>
  saveActiveSnapshot: (options?: {
    remark?: string
    skipDiagnose?: boolean
  }) => Promise<ScheduleSnapshotDetail | null>
}

/**
 * 快照管理逻辑 composable
 *
 * 职责：
 * - 快照的保存、加载、诊断
 * - 快照相关的 CRUD 操作
 */
export function useScheduleSnapshots(): UseScheduleSnapshotsReturn {
  /**
   * 获取 store 实例（延迟导入避免循环依赖）
   */
  const getStore = (): ReturnType<typeof useScheduleStore> => {
    return useScheduleStore()
  }

  /**
   * 构建快照 payload（课时数据）
   */
  function buildSnapshotPayload(
    occurrences: ScheduleOccurrenceDraft[]
  ): ScheduleSnapshotOccurrenceRequest[] {
    return occurrences.map((occurrence) => ({
      taskActivityId: occurrence.taskActivityId,
      occurrenceIndex: occurrence.occurrenceIndex,
      placed: occurrence.placed,
      dayOfWeek: occurrence.dayOfWeek ?? null,
      period: occurrence.period ?? null,
      roomId: occurrence.roomId ?? null
    }))
  }

  /**
   * 诊断当前草稿
   *
   * 调用后端 API 对当前草稿进行排课冲突诊断，返回诊断结果。
   */
  async function diagnoseCurrentDraft(): Promise<ScheduleSnapshotDiagnosis | null> {
    const store = getStore()
    if (!store.currentTask) {
      return null
    }

    const result = await scheduleApi.diagnoseSnapshot(store.currentTask.id, {
      snapshotId: store.activeSnapshot?.id ?? null,
      runId: store.latestRun?.id ?? null,
      occurrences: buildSnapshotPayload(store.draftOccurrences)
    })

    return result
  }

  /**
   * 保存快照
   *
   * 将当前草稿保存为新的快照版本。可以选择是否跳过诊断步骤。
   *
   * @param options.name 快照名称
   * @param options.remark 备注
   * @param options.skipDiagnose 是否跳过诊断（默认 false）
   * @returns 保存后的快照详情
   */
  async function saveSnapshot(options: {
    name?: string
    remark?: string
    skipDiagnose?: boolean
  } = {}): Promise<ScheduleSnapshotDetail | null> {
    const store = getStore()
    if (!store.currentTask) {
      return null
    }

    if (!options.skipDiagnose) {
      await diagnoseCurrentDraft()
    }

    store.saving = true

    try {
      const payload: ScheduleSnapshotDraftRequest = {
        name: options.name,
        remark: options.remark,
        occurrences: buildSnapshotPayload(store.draftOccurrences)
      }

      const snapshot = await scheduleApi.saveSnapshot(store.currentTask.id, payload)

      store.snapshots = [snapshot, ...store.snapshots.filter((item) => item.id !== snapshot.id)]

      const draftComposable = useScheduleDraft()
      draftComposable.markBaselineFromSave(buildDraftOccurrencesFromSnapshot(snapshot), snapshot)

      store.ensureSelections()
      return snapshot
    } finally {
      store.saving = false
    }
  }

  async function saveActiveSnapshot(options: {
    remark?: string
    skipDiagnose?: boolean
  } = {}): Promise<ScheduleSnapshotDetail | null> {
    const store = getStore()
    if (!store.currentTask || !store.activeSnapshot) {
      return null
    }

    if (!options.skipDiagnose) {
      await diagnoseCurrentDraft()
    }

    store.saving = true
    try {
      const activeSnapshotId = store.activeSnapshot.id
      const payload: ScheduleSnapshotDraftRequest = {
        remark: options.remark,
        occurrences: buildSnapshotPayload(store.draftOccurrences)
      }
      const snapshot = await scheduleApi.updateSnapshot(store.currentTask.id, activeSnapshotId, payload)
      store.snapshots = store.snapshots.map((item) => (item.id === snapshot.id ? snapshot : item))
      const draftComposable = useScheduleDraft()
      draftComposable.markBaselineFromSave(buildDraftOccurrencesFromSnapshot(snapshot), snapshot)
      store.ensureSelections()
      return snapshot
    } finally {
      store.saving = false
    }
  }

  return {
    buildSnapshotPayload,
    diagnoseCurrentDraft,
    saveSnapshot,
    saveActiveSnapshot
  }
}
